import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from 'ui/views/cards/Card';
import LabelChip from 'ui/domain/Chips/LabelChip';
import RoundedChip from 'ui/elements/Chip/RoundedChip';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { stripHtml } from 'util/stringUtils';
import { PartnerOffer } from 'domain/partnerOffers';
import { getLinkInfo } from './utils';
import CompanyProfileDialog from 'domain/companies/ProfileDialog/CompanyProfileDialog';
import { contentSpacing } from 'ui/theme/themeConstants';

interface SimpleLayoutProps {
  offer: PartnerOffer;
  openInNewTab?: boolean;
}

export default function SimpleLayout({ offer, openInNewTab }: SimpleLayoutProps) {
  const { resource: userProfile } = useSelfUserProfile();
  const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);

  const linkBehaviour = openInNewTab
    ? {
        target: 'blank',
        rel: 'noopener noreferrer',
      }
    : null;

  const userProfileData = userProfile.state === 'fetched' ? userProfile.resource : undefined;
  const linkInfo = getLinkInfo(offer, userProfileData);

  const cardContent = (
    <div className="u-flex u-flex--column u-flex-center u-flex-align-center u-flex--gap-1 u-fullHeight">
      {/* Banner Label - Top Right */}
      {offer.bannerLabel && (
        <div
          style={{
            position: 'absolute',
            top: contentSpacing,
            right: contentSpacing,
          }}
        >
          <RoundedChip label={offer.bannerLabel.name} color={offer.bannerLabel.color} />
        </div>
      )}
      <img src={offer.logoUrl} width={40} height={40} />
      <b className="text-align-center">{offer.name}</b>
      {offer.description && <p className="text-body">{stripHtml(offer.description)}</p>}

      {/* Labels - Centered */}
      {offer.labels.length > 0 && (
        <div className="u-flex u-flex-center u-flex-wrap u-content-spacing-bottom">
          {offer.labels.map(label => (
            <LabelChip
              key={label.name + label.color}
              label={label.name}
              color={label.color}
              className="u-quarter-spacing-right u-quarter-spacing-bottom"
            />
          ))}
        </div>
      )}
      <span className="text-link">{offer.linkText || 'Learn more'}</span>
    </div>
  );

  return (
    <Card
      style={{ height: '100%', position: 'relative' }}
      padding="double"
      className="u-flex u-flex--column u-flex-center u-flex-align-center u-flex--gap-1"
    >
      {' '}
      {linkInfo.isCompany ? (
        <div
          onClick={() => setIsCompanyDialogOpen(true)}
          className="u-flex u-flex--column u-fullHeight"
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsCompanyDialogOpen(true);
            }
          }}
        >
          {cardContent}
        </div>
      ) : linkInfo.isInternal ? (
        <Link to={linkInfo.href} className="u-flex u-flex--column u-fullHeight" {...linkBehaviour}>
          {cardContent}
        </Link>
      ) : (
        <a
          href={linkInfo.href}
          target="_blank"
          rel="noopener noreferrer"
          className="u-flex u-flex--column u-fullHeight"
        >
          {cardContent}
        </a>
      )}
      {isCompanyDialogOpen && offer.companyId && (
        <CompanyProfileDialog
          companyId={offer.companyId}
          onClose={() => setIsCompanyDialogOpen(false)}
          analyticsCommunityId="none"
          analyticsContext="partner-offers"
        />
      )}
    </Card>
  );
}
